import { useContext, useMemo } from 'react';
import { ItemSelectedForCropContext } from '../context-provider';
import { FEATURE_CROP_BACKGROUNDS } from '../flags';
import { getKeepAspectRatio } from '../utils/aspect-ratio';
import { getCropFromItem } from '../utils/get-crop-from-item';
import { getRectAfterCrop } from '../utils/get-dimensions-after-crop';
import { EditorStarterItem } from './item-type';

export const useCroppableLayer = ({
  item,
  rotation,
  opacity,
  borderRadius,
  cropBackground,
  zIndex,
}: {
  item: EditorStarterItem;
  rotation: number;
  opacity: number;
  borderRadius: number;
  cropBackground: boolean;
  zIndex?: number;
}) => {
  const itemSelectedForCrop = useContext(ItemSelectedForCropContext);
  const itemIsBeingCropped = item.id === itemSelectedForCrop;

  const rectAfterCrop = useMemo(() => {
    if (cropBackground) {
      return {
        left: item.left,
        top: item.top,
        width: item.width,
        height: item.height,
      };
    }

    return getRectAfterCrop(item);
  }, [item, cropBackground]);
  const crop = useMemo(() => getCropFromItem(item), [item]);

  if (!crop) {
    throw new Error('Crop not implemented for this item type');
  }

  const keepAspectRatio = getKeepAspectRatio(item);

  const innerStyle: React.CSSProperties = useMemo(() => {
    return {
      width: item.width,
      left: cropBackground ? 0 : -(crop.cropLeft * item.width),
      top: cropBackground ? 0 : -(crop.cropTop * item.height),
      height: item.height,
      position: 'absolute',
      objectFit: keepAspectRatio ? 'cover' : 'fill',
      maxWidth: 'unset',
    };
  }, [
    crop.cropLeft,
    crop.cropTop,
    item.height,
    item.width,
    cropBackground,
    keepAspectRatio,
  ]);

  const outerStyle: React.CSSProperties = useMemo(() => {
    return {
      position: 'absolute',
      left: rectAfterCrop.left,
      top: rectAfterCrop.top,
      transform: `rotate(${rotation}deg)`,
      opacity: cropBackground
        ? 0.3
        : itemIsBeingCropped && FEATURE_CROP_BACKGROUNDS
          ? 1
          : opacity,
      overflow: 'hidden',
      width: rectAfterCrop.width,
      height: rectAfterCrop.height,
      borderRadius: cropBackground ? 0 : borderRadius,
      zIndex,
      isolation: zIndex !== undefined ? 'isolate' : undefined,
    };
  }, [
    borderRadius,
    rotation,
    opacity,
    rectAfterCrop.height,
    rectAfterCrop.left,
    rectAfterCrop.top,
    rectAfterCrop.width,
    itemIsBeingCropped,
    cropBackground,
    zIndex,
  ]);

  return useMemo(() => {
    return {
      innerStyle,
      outerStyle,
    };
  }, [innerStyle, outerStyle]);
};
